import React, { useEffect, useState } from 'react';
import AddressVerification from '../AddressVerification/AddressVerification';
import env from '../../utils/env';
import { useAppContext } from '../../utils/ContextProviders/AppContext';
import useHandleFormInput from './hooks/useHandleFormInput';
import Input from './Input';
import Select from './Select';

import styles from './ShippingAddress.module.scss';
import { mapFormStateToAddresstoValidate, mapValidatedAddressToFormState, validateForm } from './shippingAddressUtils';
import { VerifiedAddress } from '../AddressVerification/addressVerificationTypes';
import { useFriendContext } from '../../utils/ContextProviders/FriendContext';
import NewsletterOptIn from './NewsletterOptIn';
import { useHandleNewsletterOptIn } from './hooks/useHandleNewsletterOptIn';
import { getGDPRStatusFromCountry } from '../../utils/Data/registerConnector';
import { trackEvent } from '../../utils/tracking';

function ShippingForm(props: { slideFormUp: () => void }) {
  const { slideFormUp } = props;
  const { countries, viewSize } = useAppContext();
  const { formState, handleOnChange, setFormState } = useHandleFormInput();
  const {
    shipCountry, shipAddress1, shipAddress2, shipCity, shipState, shipZipCode,
  } = formState;
  const { friend, setFriend } = useFriendContext();
  const [showAddressVerification, setShowAddressVerification] = useState(false);
  const { isOptIn, setIsOptIn, handleOnSelect } = useHandleNewsletterOptIn(countries, shipCountry);

  useEffect(() => {
    if (friend.isEmailOptIn) {
      setIsOptIn(friend.isEmailOptIn);
    } else if (getGDPRStatusFromCountry(countries, shipCountry)) {
      setIsOptIn(false);
    } else {
      setIsOptIn(true);
    }
  }, [shipCountry]);

  useEffect(() => {
    if (friend.address) {
      setFormState(friend.address);
    }
  }, [friend]);

  const handleAddressVerificationResponse = (address: VerifiedAddress) => {
    setFriend(Object.assign(friend, { address: mapValidatedAddressToFormState(address) }));
    trackEvent('Address Submitted', friend, viewSize, 'Save & Continue');
    slideFormUp();
  };

  const handleSaveOnClick = () => {
    setShowAddressVerification(true);
    if (getGDPRStatusFromCountry(countries, shipCountry)) {
      setFriend(Object.assign(friend, { isEmailOptIn: isOptIn }));
    }
  };

  return (
    <>
      <form className={styles[viewSize]}>
        <Select countries={countries} name="shipCountry" value={shipCountry} label="Country / Region" handleOnChange={handleOnChange} currentCountryCode={shipCountry} />
        <div className={styles.flex}>
          <Input name="shipAddress1" value={shipAddress1} label="Street Address" handleOnChange={handleOnChange} />
          <Input name="shipAddress2" value={shipAddress2} label="Apt, Suite, Room" handleOnChange={handleOnChange} />
        </div>
        <Input name="shipCity" value={shipCity} label="City" handleOnChange={handleOnChange} />
        <div className={`${styles.flex} ${styles.half}`}>
          <Select countries={countries} name="shipState" value={shipState} label="State" handleOnChange={handleOnChange} currentCountryCode={shipCountry} />
          <Input name="shipZipCode" value={shipZipCode} label="Zip Code" handleOnChange={handleOnChange} />
        </div>
        <div className={styles.divider} />
        {getGDPRStatusFromCountry(countries, shipCountry)
        && (
        <NewsletterOptIn
          isOptIn={isOptIn}
          handleOnSelect={handleOnSelect}
        />
        )}
        <button data-testid="address-save" type="button" className={styles.save} onClick={handleSaveOnClick} disabled={!validateForm(formState)}>Save &amp; Continue</button>
      </form>
      {showAddressVerification && (
      <AddressVerification
        submitAddressCallback={handleAddressVerificationResponse}
        addressToValidate={mapFormStateToAddresstoValidate(formState)}
        setShowAddressVerification={setShowAddressVerification}
        env={env.envLabel}
      />
      )}
    </>
  );
}

export default ShippingForm;
